import { signOut, User as FirebaseUser } from "firebase/auth";
import { doc, getDoc, Timestamp, writeBatch, query, where, getDocs, collection } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { User as RuchiraUser } from "../types";

/**
 * Normalize phone number to +91XXXXXXXXXX format
 */
const normalizePhone = (phone: string): string => {
  if (!phone) return "";
  
  let cleaned = phone.replace(/\s/g, "");
  if (cleaned.startsWith("+91")) cleaned = cleaned.substring(3);
  else if (cleaned.startsWith("91") && cleaned.length === 12) cleaned = cleaned.substring(2);
  
  cleaned = cleaned.replace(/\D/g, "");
  
  if (cleaned.length !== 10) {
    return phone.replace(/\s/g, ""); 
  }
  return `+91${cleaned}`;
};

/**
 * Find existing user by normalized phone.
 * NOW CHECKS 3 FORMATS: +91, 91, and Raw 10-digit
 */
export const findUserByPhone = async (phone: string): Promise<RuchiraUser | null> => {
  // 1. Get raw 10 digits
  const rawNumber = phone.replace(/\D/g, "").slice(-10);
  
  // 2. Generate all 3 common variations
  const formatNormalized = `+91${rawNumber}`; // +918639828649
  const formatNoPlus = `91${rawNumber}`;      // 918639828649 (Your DB Format)
  const formatRaw = rawNumber;                // 8639828649

  try {
    // 0. Signed-in user's own profile (always readable under the rules)
    const me = auth.currentUser;
    if (me) {
      const own = await getDoc(doc(db, "users", me.uid));
      if (own.exists()) return { uid: own.id, ...own.data() } as RuchiraUser;
    }
  } catch (e) {
    console.warn("Own profile lookup failed:", e);
  }

  try {
    // 1. Try Mapping Collection (Fastest) using the standard +91 format
    const mappingRef = doc(db, "phone_mappings", formatNormalized);
    const snap = await getDoc(mappingRef);
    if (snap.exists()) {
      const mappedUid = snap.data().uid;
      const userSnap = await getDoc(doc(db, "users", mappedUid));
      if (userSnap.exists()) return { uid: userSnap.id, ...userSnap.data() } as RuchiraUser;
    }

    // 2. Fallback: Direct Query (Checks ALL 3 formats at once)
    const usersRef = collection(db, "users");
    const q = query(
      usersRef, 
      where("phone", "in", [formatNormalized, formatNoPlus, formatRaw]) 
    );
    
    const querySnap = await getDocs(q);
    if (!querySnap.empty) {
      console.log("✅ Found user via fallback query");
      const doc = querySnap.docs[0];
      return { uid: doc.id, ...doc.data() } as RuchiraUser;
    }

  } catch (e) {
    console.warn("User lookup failed:", e);
  }
  return null;
};

export const createUserProfile = async (
  firebaseUser: FirebaseUser,
  profileData: Partial<RuchiraUser>
) => {
  const uid = firebaseUser.uid;
  const incomingPhone = firebaseUser.phoneNumber || profileData.phone || "";
  
  if (!incomingPhone) throw new Error("Phone number is required");
  
  const normalizedPhone = normalizePhone(incomingPhone);
  const batch = writeBatch(db);
  const userRef = doc(db, "users", uid);
  
  // Check if user exists (by UID)
  const docSnap = await getDoc(userRef);
  let existingUser = docSnap.exists() ? (docSnap.data() as RuchiraUser) : null;

  // Migration Check: If no user by UID, check by Phone
  if (!existingUser) {
    const foundByPhone = await findUserByPhone(normalizedPhone);
    if (foundByPhone) {
       console.log("Found existing profile by phone, linking...");
       return foundByPhone; 
    }
  }

  if (existingUser) {
    // UPDATE — profile fields only. Roles / wallet fields are server-managed and the rules
    // reject client changes to them, so never re-send them here.
    const { roles: _r, walletBalance: _w, ...safeProfile } = (profileData || {}) as any;
    batch.set(userRef, {
      ...safeProfile,
      phone: normalizedPhone, // We standardize the phone on update
      updatedAt: Timestamp.now(),
    }, { merge: true });

  } else {
    // CREATE
    const newUser: RuchiraUser = {
      uid,
      name: profileData.name || "User",
      phone: normalizedPhone,
      address: profileData.address || "",
      email: profileData.email || firebaseUser.email || "",
      roles: ["customer"],
      currentRole: "customer",
      preferences: profileData.preferences || {},
      createdAt: Timestamp.now(),
      walletBalance: 0,
      communicationChannels: ["whatsapp"]
    };
    
    batch.set(userRef, newUser);
  }
  
  // Create Mapping for future fast lookups
  const mappingRef = doc(db, "phone_mappings", normalizedPhone);
  batch.set(mappingRef, { uid: uid });
  
  await batch.commit();
  return { uid, ...profileData };
};

export { normalizePhone };

export const logout = async () => {
  await signOut(auth);
};